import React, { ReactNode } from 'react'
import {
    DataTable,
    DataTableOpenPanelButton,
    DataTableScope,
    DataTableToolbar,
    getNoMatchesText,
    useDataTableFilters,
    useDataTableFiltersState,
} from '../../../../shared/components/DataTable'
import { EmptyPlaceholder } from '../../../../shared/components/EmptyPlaceholder'
import useActivityStatuses from '../../../../shared/hooks/useActivityStatuses'
import useEvidenceDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useEvidenceDisplayName'
import useStrategicImperativeDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useStrategicImperativeDisplayName'
import useTppDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useTppDisplayName'
import useValueMessageDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useValueMessageDisplayName'
import { useIsRoadmapReadonly } from '../../../../shared/hooks/useIsRoadmapReadonly'
import { useIsSnapshotMode } from '../../../../shared/hooks/useIsSnapshotMode'
import { getActivityColumns } from '../ActivityTable.columns'
import { getActivityFilterDefinitions, getActivitySearchValues } from '../ActivityTable.filters'
import { ActivityColumnId, ActivityDisplayNames, ActivityRow } from '../ActivityTable.types'
import { renderActivityCell } from './renderActivityCell'

export type ActivityTabTableProps = {
    rows: ActivityRow[]
    scope: DataTableScope
    defaultColumnIds: ReadonlySet<ActivityColumnId>
    searchPlaceholder: string
    emptyTitle: string
    emptySubtitle: ReactNode
    noMatchesLabel: string
    onOpenPanel: (activityId: string) => void
    onReorder?: (rows: ActivityRow[]) => void
}

export const ActivityTabTable = ({
    rows,
    scope,
    defaultColumnIds,
    searchPlaceholder,
    emptyTitle,
    emptySubtitle,
    noMatchesLabel,
    onOpenPanel,
    onReorder,
}: ActivityTabTableProps) => {
    const { getActivityStatusDetails } = useActivityStatuses()
    const displayNames: ActivityDisplayNames = {
        valueMessages: useValueMessageDisplayName().plural,
        strategicImperatives: useStrategicImperativeDisplayName().plural,
        tpp: useTppDisplayName().singular,
        evidence: useEvidenceDisplayName().plural,
    }
    const isSnapshotMode = useIsSnapshotMode()
    const isRoadmapReadonly = useIsRoadmapReadonly()

    const filtersState = useDataTableFiltersState(scope)

    const { toolbarProps, hiddenColumnIds, hasActiveFilters, visibleRows } = useDataTableFilters({
        rows,
        filterDefinitions: getActivityFilterDefinitions(getActivityStatusDetails, displayNames, defaultColumnIds),
        getSearchValues: getActivitySearchValues,
        state: filtersState,
    })

    return (
        <DataTable
            showIndexColumn
            hasStickyColumns
            columns={getActivityColumns(displayNames)}
            rows={visibleRows}
            getRowId={(activity) => activity._id}
            renderCell={renderActivityCell(getActivityStatusDetails)}
            hiddenColumnIds={hiddenColumnIds}
            renderRowActions={(activity) => (
                <DataTableOpenPanelButton
                    accessibleLabel={`Open ${activity.title}`}
                    onClick={() => onOpenPanel(activity._id)}
                />
            )}
            reorder={
                onReorder && {
                    isDisabled: isSnapshotMode || isRoadmapReadonly || hasActiveFilters,
                    onReorder,
                }
            }
            toolbar={<DataTableToolbar {...toolbarProps} searchPlaceholder={searchPlaceholder} filterMenuWidth={260} />}
            emptyState={
                <EmptyPlaceholder
                    title={hasActiveFilters ? getNoMatchesText(noMatchesLabel) : emptyTitle}
                    subtitle={hasActiveFilters ? 'Clear the search or filters to see every row again.' : emptySubtitle}
                    sx={{ minHeight: 280 }}
                />
            }
        />
    )
}

export default ActivityTabTable
